// src/pages/admin-accounts/AdminAccountsListPage/AdminAccountsListPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import AdminAccountsAPI from '../../../api/v3/admin-accounts-api-v3'

const BASE = '/app/sales/bagspa/admin-accounts'

const ROLE_OPTIONS = [
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'admin', label: 'Admin' },
  { value: 'sales_ops', label: 'Sales Ops' },
  { value: 'finance', label: 'Finance' }
]

const getRoleLabel = (role) => {
  const found = ROLE_OPTIONS.find((item) => item.value === role)
  return found?.label || role || '-'
}

export default function AdminAccountsListPage() {
  const history = useHistory()

  const [rows, setRows] = useState([])
  const [roles, setRoles] = useState(ROLE_OPTIONS)
  const [meta, setMeta] = useState({ page: 1, per: 20, total: 0, total_pages: 1 })

  const [filter, setFilter] = useState({ q: '', role: '' })
  const [appliedFilter, setAppliedFilter] = useState({ q: '', role: '' })
  const [page, setPage] = useState(1)
  const [per, setPer] = useState(20)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const loadRoles = useCallback(async () => {
    try {
      const json = await AdminAccountsAPI.roles()
      if (Array.isArray(json?.roles) && json.roles.length > 0) {
        setRoles(json.roles)
      }
    } catch (_) {
      setRoles(ROLE_OPTIONS)
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const json = await AdminAccountsAPI.list({
        q: appliedFilter.q,
        role: appliedFilter.role,
        page,
        per,
        sort_by: 'created_at',
        sort_dir: 'desc'
      })

      setRows(Array.isArray(json?.items) ? json.items : [])
      setMeta(json?.meta || { page, per, total: 0, total_pages: 1 })
    } catch (e) {
      setError(e.message || 'Failed to fetch admin accounts')
    } finally {
      setLoading(false)
    }
  }, [appliedFilter, page, per])

  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  useEffect(() => {
    load()
  }, [load])

  const totalPages = useMemo(() => {
    return Number(meta.total_pages || Math.max(1, Math.ceil((meta.total || 0) / (meta.per || per || 20))))
  }, [meta, per])

  const applyFilter = () => {
    setPage(1)
    setAppliedFilter({
      q: filter.q.trim(),
      role: filter.role
    })
  }

  const resetFilter = () => {
    const emptyFilter = { q: '', role: '' }
    setFilter(emptyFilter)
    setAppliedFilter(emptyFilter)
    setPage(1)
  }

  const handleDelete = async (admin) => {
    if (!window.confirm(`Delete admin account ${admin.email}?`)) return

    try {
      setLoading(true)
      setError('')
      await AdminAccountsAPI.destroy(admin.id)
      await load()
    } catch (e) {
      setError(e.message || 'Failed to delete admin account')
    } finally {
      setLoading(false)
    }
  }

  const canPrev = Number(meta.page || page) > 1
  const canNext = Number(meta.page || page) < totalPages

  return (
    <div className="bagspa-page admin-accounts-page">
      <div
        className="d-flex justify-content-between align-items-center mb-3"
        style={{ gap: 8, flexWrap: 'wrap' }}
      >
        <div>
          <h3 className="mb-0">Admin Accounts</h3>
          <div className="text-muted small">
            Manage dashboard BagSpa admin accounts.
          </div>
        </div>

        <Link className="btn btn-primary" to={`${BASE}/new`}>
          Create New Admin
        </Link>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-5 mb-3 mb-md-0">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search name / email"
                value={filter.q}
                onChange={(e) => setFilter((current) => ({ ...current, q: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilter()
                }}
              />
            </div>

            <div className="col-md-3 mb-3 mb-md-0">
              <label className="form-label">Role</label>
              <select
                className="form-control"
                value={filter.role}
                onChange={(e) => setFilter((current) => ({ ...current, role: e.target.value }))}
              >
                <option value="">All Roles</option>
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2 mb-3 mb-md-0">
              <label className="form-label">Rows</label>
              <select
                className="form-control"
                value={per}
                onChange={(e) => {
                  setPer(Number(e.target.value) || 20)
                  setPage(1)
                }}
              >
                {[10, 20, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}/page
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2 d-flex" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary flex-fill"
                onClick={applyFilter}
                disabled={loading}
              >
                Search
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary flex-fill"
                onClick={resetFilter}
                disabled={loading}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Role</th>
                  <th style={{ width: 180 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      Loading...
                    </td>
                  </tr>
                ) : null}

                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No admin account found.
                    </td>
                  </tr>
                ) : null}

                {!loading && rows.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.email || '-'}</td>
                    <td>{admin.first_name || '-'}</td>
                    <td>{admin.last_name || '-'}</td>
                    <td>
                      <span className="badge badge-secondary">
                        {admin.role_label || getRoleLabel(admin.role)}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 mr-3"
                        onClick={() => history.push(`${BASE}/${admin.id}/edit`)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <div className="text-muted small">
              Page {meta.page || page} of {totalPages} • Total {meta.total || 0}
            </div>

            <div className="btn-group">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={!canPrev || loading}
                onClick={() => setPage(1)}
              >
                &laquo;
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={!canPrev || loading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                &lsaquo; Prev
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={!canNext || loading}
                onClick={() => setPage((current) => current + 1)}
              >
                Next &rsaquo;
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={!canNext || loading}
                onClick={() => setPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}